/**
 * MeetingsTab
 *
 * Role-aware supervision meetings tab:
 * - Supervisee (provider role): "My Supervision Notes" table + "New Note" button.
 * - Supervisor role: "My Supervisor Notes" + "New Supervisor Note" + "Supervision Signing" + "Signed Supervision Notes".
 *
 * Creating a note navigates to /dashboard/supervision/meetings/new?type=...
 *   (supervision | supervisor | group-supervision | group-supervisor)
 * Opening an existing note navigates to /dashboard/supervision/meetings/:responseId
 * Supervisor review/cosign navigates to the existing /dashboard/supervision/review/:documentId page.
 */

import { Alert, Button, Group, Loader, Menu, Paper, Stack, Text, Title } from '@mantine/core';
import { IconChevronDown, IconFileText, IconPencilPlus, IconSignature } from '@tabler/icons-react';
import type { Questionnaire } from '@medplum/fhirtypes';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useResolvedTemplates } from '../../settings/template-library/hooks';
import { useSupervisionMeetingNotes } from '../hooks/useSupervisionMeetingNotes';
import { useSupervisionScope } from '../hooks/useSupervisionScope';
import type { SupervisionMeetingNoteItem } from '../hooks/useSupervisionMeetingNotes';
import { SupervisionMeetingTable } from '../components/SupervisionMeetingTable';

const MEETINGS_BASE = '/dashboard/supervision/meetings';

type SupervisionNoteTypeParam = 'supervision' | 'supervisor' | 'group-supervision' | 'group-supervisor';

interface NewSupervisionNoteMenuProps {
  label: string;
  options: { label: string; templateId: string; type: SupervisionNoteTypeParam }[];
  loading: boolean;
  onSelect: (templateId: string, type: SupervisionNoteTypeParam) => void;
}

function NewSupervisionNoteMenu({
  label,
  options,
  loading,
  onSelect,
}: NewSupervisionNoteMenuProps): JSX.Element {
  return (
    <Menu position="bottom-end" withinPortal>
      <Menu.Target>
        <Button
          leftSection={<IconPencilPlus size={16} />}
          rightSection={<IconChevronDown size={16} />}
          size="sm"
          loading={loading}
          disabled={!loading && options.length === 0}
        >
          {label}
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        {options.map((option) => (
          <Menu.Item
            key={option.templateId}
            onClick={() => onSelect(option.templateId, option.type)}
          >
            {option.label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

export function MeetingsTab(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { role } = useSupervisionScope();
  const isSupervisor = role === 'supervisor';
  const {
    templates: administrativeTemplates,
    loading: templatesLoading,
    error: templatesError,
  } = useResolvedTemplates({ target: 'admin-note', category: 'administrative' });

  const { myNotes, signingQueue, signedSupervisionNotes, loading, error, refetch } = useSupervisionMeetingNotes();

  const meetingTemplateOptions = useMemo(
    () =>
      administrativeTemplates.flatMap((template: Questionnaire) => {
        if (!template.id) return [];
        const identity = [
          template.name,
          template.id,
          template.url?.split('/').pop(),
          template.title,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        const isGroup = identity.includes('group');
        const type: SupervisionNoteTypeParam = isSupervisor
          ? isGroup
            ? 'group-supervisor'
            : 'supervisor'
          : isGroup
            ? 'group-supervision'
            : 'supervision';
        return [{
          label: template.title ?? template.name ?? 'Untitled template',
          templateId: template.id,
          type,
        }];
      }),
    [administrativeTemplates, isSupervisor],
  );

  const isMeetingsListRoute =
    location.pathname === MEETINGS_BASE || location.pathname === `${MEETINGS_BASE}/`;

  useEffect(() => {
    if (!isMeetingsListRoute) return;
    void refetch();
  }, [isMeetingsListRoute, location.key, refetch]);

  const handleOpenNote = useCallback(
    (item: SupervisionMeetingNoteItem) => {
      navigate(`${MEETINGS_BASE}/${item.id}`);
    },
    [navigate],
  );

  const handleOpenForReview = useCallback(
    (item: SupervisionMeetingNoteItem) => {
      // Supervisor reviews supervisee notes via the existing DocumentReviewPage.
      navigate(`/dashboard/supervision/review/${item.id}`);
    },
    [navigate],
  );

  const handleNewNote = useCallback(
    (templateId: string, type: SupervisionNoteTypeParam) => {
      navigate(`${MEETINGS_BASE}/new?type=${type}&template=${encodeURIComponent(templateId)}`);
    },
    [navigate],
  );

  if (loading) {
    return (
      <Stack gap="md">
        <Group gap="sm">
          <Loader size="sm" />
          <Text size="sm">Loading supervision meetings…</Text>
        </Group>
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert color="red" title="Error loading supervision meetings">
        {error}{' '}
        <Button variant="subtle" size="compact-sm" onClick={refetch}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <Stack gap="xl">
      {templatesError && (
        <Alert color="red" title="Could not load meeting note templates">
          {templatesError}
        </Alert>
      )}
      {/* ── Supervisor: My Supervisor Notes ── */}
      {isSupervisor && (
        <Stack gap="md">
          <Group justify="space-between" align="center">
            <Group gap="xs">
              <IconFileText size={18} />
              <Title order={4}>My Supervisor Notes</Title>
            </Group>
            <NewSupervisionNoteMenu
              label="New Supervisor Note"
              options={meetingTemplateOptions}
              loading={templatesLoading}
              onSelect={handleNewNote}
            />
          </Group>
          <Paper p="lg" withBorder>
            {myNotes.length === 0 ? (
              <Text c="gray.6" size="sm" ta="center" py="md">
                No supervisor notes yet. Click "New Supervisor Note" to create one.
              </Text>
            ) : (
              <div style={{ overflow: 'auto', maxHeight: 'min(420px, 55vh)' }}>
                <SupervisionMeetingTable
                  items={myNotes}
                  onRowClick={handleOpenNote}
                  actionLabel="Open"
                />
              </div>
            )}
          </Paper>
        </Stack>
      )}

      {/* ── Supervisee: My Supervision Notes ── */}
      {!isSupervisor && (
        <Stack gap="md">
          <Group justify="space-between" align="center">
            <Group gap="xs">
              <IconFileText size={18} />
              <Title order={4}>My Supervision Notes</Title>
            </Group>
            <NewSupervisionNoteMenu
              label="New Supervision Note"
              options={meetingTemplateOptions}
              loading={templatesLoading}
              onSelect={handleNewNote}
            />
          </Group>
          <Paper p="lg" withBorder>
            {myNotes.length === 0 ? (
              <Text c="gray.6" size="sm" ta="center" py="md">
                No supervision notes yet. Click "New Supervision Note" to create one.
              </Text>
            ) : (
              <div style={{ overflow: 'auto', maxHeight: 'min(420px, 55vh)' }}>
                <SupervisionMeetingTable
                  items={myNotes}
                  onRowClick={handleOpenNote}
                  actionLabel="Open"
                />
              </div>
            )}
          </Paper>
        </Stack>
      )}

      {/* ── Supervisor: Supervision Signing queue ── */}
      {isSupervisor && (
        <Stack gap="md">
          <Group gap="xs">
            <IconSignature size={18} />
            <Title order={4}>Supervision Signing</Title>
            {signingQueue.length > 0 && (
              <Text size="xs" c="gray.6">
                ({signingQueue.length} awaiting your signature)
              </Text>
            )}
          </Group>
          <Paper p="lg" withBorder>
            {signingQueue.length === 0 ? (
              <Text c="gray.6" size="sm" ta="center" py="md">
                No supervision notes awaiting your signature.
              </Text>
            ) : (
              <div style={{ overflow: 'auto', maxHeight: 'min(420px, 55vh)' }}>
                <SupervisionMeetingTable
                  items={signingQueue}
                  onRowClick={handleOpenForReview}
                  actionLabel="Review & Sign"
                  showAuthorColumn
                />
              </div>
            )}
          </Paper>
        </Stack>
      )}

      {/* ── Supervisor: Signed supervision notes (revisit cosigned supervisee notes) ── */}
      {isSupervisor && (
        <Stack gap="md">
          <Group gap="xs">
            <IconFileText size={18} />
            <Title order={4}>Signed Supervision Notes</Title>
          </Group>
          <Paper p="lg" withBorder>
            {signedSupervisionNotes.length === 0 ? (
              <Text c="gray.6" size="sm" ta="center" py="md">
                No signed supervision notes yet. Notes appear here after you co-sign them.
              </Text>
            ) : (
              <div style={{ overflow: 'auto', maxHeight: 'min(420px, 55vh)' }}>
                <SupervisionMeetingTable
                  items={signedSupervisionNotes}
                  onRowClick={handleOpenForReview}
                  actionLabel="View"
                  showAuthorColumn
                />
              </div>
            )}
          </Paper>
        </Stack>
      )}
    </Stack>
  );
}
